/**
 * The shipped theme catalog: the three static Edge built-ins plus a curated set
 * of starter presets. Each preset is just 8 base roles — the derivation engine
 * expands them. Adding a preset is one entry here.
 */
import { hexToHsl, formatHslToken } from './color'
import { BUILTIN_BASE_ROLES } from './theme-model'
import type { ThemeOption } from './theme-runtime'

/**
 * The static Edge themes. `builtin: true` → applied via CSS class only, so the
 * hand-tuned palette in index.css is preserved (zero visual regression). The
 * `roles` are for the picker's swatch preview.
 */
export const BUILTIN_THEMES: ThemeOption[] = [
  {
    id: 'dark',
    name: 'Edge Dark',
    baseMode: 'dark',
    roles: BUILTIN_BASE_ROLES.dark,
    builtin: true,
  },
  {
    id: 'light',
    name: 'Edge Light',
    baseMode: 'light',
    roles: BUILTIN_BASE_ROLES.light,
    builtin: true,
  },
  {
    id: 'black',
    name: 'Edge Black',
    baseMode: 'black',
    roles: BUILTIN_BASE_ROLES.black,
    builtin: true,
  },
]

/**
 * Edge Gray: Neon's gray surfaces and white text and accent, with Edge Dark's data colours (green up,
 * red down, amber warning) in the grids and charts. Candles, sessions and chart series already follow
 * the dark mode it is based on. Listed with the Edge themes.
 */
const EDGE_GRAY: ThemeOption = {
  id: 'edge-gray',
  name: 'Edge Gray',
  baseMode: 'dark',
  roles: {
    background: '#141619',
    foreground: '#F0F3F7',
    accent: '#F0F3F7',
    rise: BUILTIN_BASE_ROLES.dark.rise,
    fall: BUILTIN_BASE_ROLES.dark.fall,
    warning: BUILTIN_BASE_ROLES.dark.warning,
  },
  overrides: {
    // #141414, a neutral dark gray: the canvas derived from the gray ground sits 4 points under
    // it and reads near black (Chris, 2026-10-08).
    'chart-canvas': '0 0% 7.8%',
  },
}

/** The Edge family in picker order: the static built-ins, then the derived Edge Gray. */
export const EDGE_THEMES: ThemeOption[] = [...BUILTIN_THEMES, EDGE_GRAY]

/**
 * Overrides that paint secondary text (labels, top-nav tabs, chart indicator
 * legends) in the theme's Text colour instead of the derived muted mix. On warm
 * or tinted grounds that mix drifts off-hue (tan on Noir, green on Dawn), so the
 * labels stop matching the numbers beside them (Chris, 2026-09-25).
 */
function textAsMuted(foreground: string): Record<string, string> {
  const token = formatHslToken(hexToHsl(foreground))
  return { 'muted-foreground': token, 'chart-text-muted': token }
}

/** Curated starter presets — derived from 8 base roles (see the preview artifact). */
export const STARTER_PRESETS: ThemeOption[] = [
  {
    id: 'glacier',
    name: 'Glacier',
    baseMode: 'dark',
    roles: {
      background: '#1F2226',
      foreground: '#F0F3F7',
      accent: '#2EE7FF',
      rise: '#F0F3F7',
      fall: '#2EE7FF',
      warning: '#F5A623',
    },
  },
  {
    id: 'dawn',
    name: 'Dawn',
    baseMode: 'dark',
    roles: {
      background: '#1C222F',
      foreground: '#E6E1D3',
      accent: '#E6E1D3',
      rise: '#319942',
      fall: '#D55039',
      warning: '#E6A23C',
    },
    overrides: textAsMuted('#E6E1D3'),
  },
  {
    id: 'neon',
    name: 'Neon',
    baseMode: 'dark',
    roles: {
      background: '#141619',
      foreground: '#F0F3F7',
      accent: '#F0F3F7',
      rise: '#FFEA3D',
      fall: '#FF278D',
      warning: '#F5A623',
    },
  },
  // Parked 2026-09-15: cyan-on-deep-blue, no documented basis. It reads as a
  // high-contrast accessibility theme but matches no published palette, and its
  // accent and rise are the same cyan, so the brand colour and "up" are
  // indistinguishable. An unknown id falls back to the default theme
  // (settings-context resolveOption), so anyone persisted on it lands on Dark.
  // {
  //   id: 'nebula',
  //   name: 'Nebula',
  //   baseMode: 'dark',
  //   roles: {
  //     background: '#0200A8',
  //     foreground: '#51FFFF',
  //     accent: '#51FDFE',
  //     rise: '#51FDFE',
  //     fall: '#A800A9',
  //     warning: '#FFC24B',
  //   },
  // },
  {
    id: 'sahara',
    name: 'Sahara',
    baseMode: 'light',
    roles: {
      background: '#FCFAF7',
      foreground: '#2C1810',
      accent: '#C0501A',
      rise: '#8A6408',
      fall: '#8B4513',
      warning: '#A86A00',
    },
    overrides: {
      ...textAsMuted('#2C1810'),
      // The derived light stripe sits 1 step off a card that clamps at 100%, so
      // alternating rows vanish. 95% matches Edge Light's visible step.
      'grid-row-alt': '36 45% 95%',
      // The #FCFAF7 ground (98%) sits 2 points under the 100% card, so anything
      // painted in --background on a panel (header pills, ticker search, inputs)
      // vanished. 95% gives a wider step than Edge Light's 96/100.
      background: '36 45% 95%',
    },
  },
  {
    id: 'noir',
    name: 'Noir',
    baseMode: 'dark',
    roles: {
      background: '#100F0E',
      foreground: '#FFF5E6',
      accent: '#886139',
      rise: '#67853D',
      fall: '#B17020',
      warning: '#C9972B',
    },
    overrides: textAsMuted('#FFF5E6'),
  },
]

/** The Edge themes + starters, in picker order. */
export const CATALOG_THEMES: ThemeOption[] = [...EDGE_THEMES, ...STARTER_PRESETS]

/** The default theme id when nothing is persisted. */
export const DEFAULT_THEME_ID = 'dark'

/**
 * v1 ships curated presets only. The custom-theme editor (color pickers,
 * New theme, fork-on-edit, rename/delete) is built and tested but gated OFF
 * until per-user server sync + derivation hardening land — flip to `true`
 * (or wire to a plan/flag) to unlock it for v1.1.
 */
export const CUSTOM_THEMES_ENABLED = false
